import React, { Fragment, useEffect, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { CustomActionProps, GenericTable } from '../../components/_EOS/Table'
import { IPickingRoute } from '../../interfaces/PickingRoute/PickingRoute'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { IconButton, Tooltip, Typography } from '@mui/material'
import { deletePickingRoute, getPickingRoutes } from './api'
import { useZones } from '../../hooks'
import { useTypesPickingRoute } from '../../hooks/useTypePickingRoute'
import {
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  SupervisedUserCircle as SupervisedUserCircleIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material'
import {
  getPickingRouteAdminHeaderConfiguration,
  PickingRouteAdminFilters,
  pickingRouteinitialFilters,
  pickingRouteTypesCantBeDeleted
} from './config'
import { PickingRouteAdminSearch } from './PickingRouteAdminSearch'
import { Modal } from '../../components/_EOS/Modal'
import { PickingRouteAdminReview } from '../../components/PickingRoute_Context/screens/GridElementRoutePickingAdmin/PickingRouteAdminReview'
import { useModal } from '../../hooks/useModal'
import { PickingRouteAdminEditLpn } from '../../components/PickingRoute_Context/screens/GridElementRoutePickingAdmin/PickingRouteAdminEditLpn'
import { eTypePickingRoute } from '../../enums/eTypePickingRoute'
import { PickingRouteUserReassign } from './modals/PickingRouteUserReassign'
import { usePagination } from '../../hooks/usePagination'
import { configDateInitial } from '../../utils/dateHelper'
import { Status } from '../../hooks/useFetchState'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import PickingRouteActionsBar from './PickingRouteActionsBar'
import { ePickingRouteStates } from '../../components/PickingValidate/api/types'

interface PickingRouteAdminPageProps {}

export const PickingRouteAdminPage: React.FC<
  PickingRouteAdminPageProps
> = () => {
  const [selectedElement, setSelectedElement] = React.useState<number[]>([])

  const { callApi, state } = useApi<ApiResponse<IPickingRoute>>()
  const { callApi: deletePickingRouteCallApi, state: deletePickingRouteState } =
    useApi()

  const pagination = usePagination(5)

  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const [filters, setFilters] = useState<PickingRouteAdminFilters>(
    pickingRouteinitialFilters
  )

  const {
    zonesState: { zones }
  } = useZones()

  const {
    typesPickingRouteState: { typesPickingRoute },
    getTypesPickingRoute
  } = useTypesPickingRoute()

  useEffect(() => {
    if (typesPickingRoute?.length === 0) {
      getTypesPickingRoute()
    }
  }, [])

  const requestGetPickingRoute = (
    page: number,
    pageSize: number,
    filters: PickingRouteAdminFilters
  ) => {
    callApi(getPickingRoutes(page, pageSize, filters))
  }

  const reviewPickingRouteModalState = useModal<IPickingRoute>(
    'Detalles de Ruta de Picking',
    false,
    'md'
  )
  const editPickingRouteLPNModalState = useModal<IPickingRoute>(
    'Editar LPN',
    false,
    'md'
  )
  const reassignPickerUserModal = useModal<IPickingRoute>(
    'Reasignar ruta de picking'
  )
  const deletePickingRouteModal = useModal<number>('Eliminar ruta de picking')

  const PickingRouteAdminActions: React.FC<
    CustomActionProps<IPickingRoute>
  > = ({ id, row }) => {
    return (
      <Fragment>
        <Tooltip title='Eliminar Ruta' aria-label='eliminar'>
          <IconButton
            aria-label='delete'
            onClick={() => deletePickingRouteModal.openModal('sm', id)}
            disabled={
              pickingRouteTypesCantBeDeleted.includes(row.typePickingRouteId) ||
              row.pickingRouteStateId !== ePickingRouteStates.Creada
            }
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title='Gestionar Ruta' aria-label='reasignar'>
          <IconButton
            aria-label='reasignar'
            onClick={() => reassignPickerUserModal.openModal('md', row)}
            disabled={
              row.pickingRouteStateId !== 2 ||
              row.typePickingRouteId === eTypePickingRoute.ExternalStorage
            }
          >
            <SupervisedUserCircleIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Ver Detalle' aria-label='detalle'>
          <IconButton
            aria-label='view'
            onClick={() =>
              reviewPickingRouteModalState.openModal(
                'md',
                row,
                `Ruta de Picking ${row.pickingRouteId}`
              )
            }
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Cambiar N° LPN/Tote' aria-label='Cambiar'>
          <IconButton
            aria-label='Cambiar'
            onClick={() => editPickingRouteLPNModalState.openModal('md', row)}
            disabled={
              row.typePickingRouteId === eTypePickingRoute.ExternalStorage ||
              (row.pickingRouteStateId !== 2 &&
                row.pickingRouteStateId !== 1) ||
              (row.lpn || '').trim().length === 0
            }
          >
            <ArchiveIcon />
          </IconButton>
        </Tooltip>
      </Fragment>
    )
  }

  // Configuraciones de encabezados de tabla generica
  const configs = getPickingRouteAdminHeaderConfiguration(
    zones || [],
    typesPickingRoute || []
  )

  const handleChange = (
    e: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target
    setFilters((prevFilters) => ({ ...prevFilters, [name!]: value }))
  }

  const onPickingRouteAlertClick = () => {
    // setear filtros
    const filters = {
      ...pickingRouteinitialFilters,
      initialDate: configDateInitial((new Date().getFullYear() - 2020) * 365),
      endDate: configDateInitial(0),
      pickingRouteStateId: 3,
      validationStatusId: 2,
      isIncomplete: true
    }

    setFilters(filters)
    // hacer request

    requestGetPickingRoute(0, pagination.pageSize, filters)
  }

  const requestDeletePickingRoute = (pickingRouteId: number) => {
    deletePickingRouteCallApi(deletePickingRoute([pickingRouteId]))
      .then(() => {
        // refrescar grilla
        requestGetPickingRoute(0, pagination.pageSize, filters)
        // mostrar mensaje
        openSnackBar('Ruta de Picking eliminada correctamente', true)
        // cerra modal
        deletePickingRouteModal.closeModal()
      })
      .catch((err) => {
        openSnackBar(`No fue posible eliminar ruta de picking`, false)
        console.log(err)
      })
  }

  const requestMasiveDeletePickingRoute = () => {
    deletePickingRouteCallApi(deletePickingRoute(selectedElement))
      .then((response: { message: string }) => {
        // refrescar grilla
        requestGetPickingRoute(0, pagination.pageSize, filters)
        // mostrar mensaje
        openSnackBar(JSON.stringify(response.message, null, 2), true)
        // cerra modal
        deletePickingRouteModal.closeModal()
        setSelectedElement([])
      })
      .catch((err) => openSnackBar(err, false))
  }

  const handleCheckAllToggle = (selectAll: boolean) => {
    if (selectAll) {
      // Selecciona todos los IDs de las filas actualmente visibles
      const newCheckedElements = state.data?.items.map((item) => item.id) || []
      setSelectedElement(newCheckedElements)
    } else {
      // Limpia la selección
      setSelectedElement([])
    }
  }

  const handleCheckChange = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedElement((prev) => [...prev, id])
    } else {
      setSelectedElement((prev) => [...prev.filter((el) => el !== id)])
    }
  }

  return (
    <Fragment>
      <StandardPage pageTitle='Administrador de Rutas de Picking'>
        <PickingRouteAdminSearch
          handleSearch={() =>
            requestGetPickingRoute(
              pagination.page,
              pagination.pageSize,
              filters
            )
          }
          initialFilters={filters}
          setFilters={setFilters}
          handleChange={handleChange}
        />

        <PickingRouteActionsBar
          onPickingRouteAlertClick={onPickingRouteAlertClick}
          requestMasiveDeletePickingRoute={requestMasiveDeletePickingRoute}
          disableDeleteButton={selectedElement.length === 0}
        />

        <GenericTable<IPickingRoute>
          isLoading={state.status === Status.Loading}
          headersConfig={configs}
          customActionComponent={PickingRouteAdminActions}
          IDKey={'pickingRouteId'}
          data={state.data?.items || []}
          pagination={{
            page: pagination.page || 0,
            pageSize: pagination.pageSize || 5,
            totalItem: state.data?.totalItems || 0
          }}
          onPaginationChange={(page, pageSize) => {
            pagination.setPagination(page, pageSize)
            requestGetPickingRoute(page, pageSize, filters)
          }}
          showCheckboxes={true}
          onCheckChange={handleCheckChange}
          checkedElements={selectedElement}
          onCheckAll={handleCheckAllToggle}
        />
      </StandardPage>

      {/* MODALS */}
      <Modal
        open={reviewPickingRouteModalState.isOpen}
        onClose={reviewPickingRouteModalState.closeModal}
        title={reviewPickingRouteModalState.title}
      >
        <PickingRouteAdminReview
          pickingRoute={reviewPickingRouteModalState.extendedProps}
        />
      </Modal>
      <Modal
        open={editPickingRouteLPNModalState.isOpen}
        onClose={editPickingRouteLPNModalState.closeModal}
        title={editPickingRouteLPNModalState.title}
      >
        {editPickingRouteLPNModalState &&
          editPickingRouteLPNModalState.extendedProps && (
            <PickingRouteAdminEditLpn
              pickingRouteId={
                editPickingRouteLPNModalState.extendedProps?.pickingRouteId
              }
              lpn={editPickingRouteLPNModalState.extendedProps?.lpn}
              fetchDataGrid={() =>
                requestGetPickingRoute(
                  pagination.page,
                  pagination.pageSize,
                  filters
                )
              }
            />
          )}
      </Modal>
      <PickingRouteUserReassign
        modalState={reassignPickerUserModal}
        handleShowSnackbar={openSnackBar}
        fetchDataGrid={() =>
          requestGetPickingRoute(pagination.page, pagination.pageSize, filters)
        }
      />
      <Modal
        loading={deletePickingRouteState.status === Status.Loading}
        open={deletePickingRouteModal.isOpen}
        onClose={deletePickingRouteModal.closeModal}
        maxWidth={deletePickingRouteModal.size}
        title='Eliminar Ruta de Picking'
        cancelButton={{
          title: 'Cancelar',
          action: deletePickingRouteModal.closeModal
        }}
        successButton={{
          title: 'Aceptar',
          action: () =>
            requestDeletePickingRoute(
              deletePickingRouteModal.extendedProps || 0
            )
        }}
      >
        <Typography style={{ textAlign: 'center' }}>
          {`¿Está seguro que desea eliminar la ruta ${deletePickingRouteModal.extendedProps}?`}
        </Typography>
      </Modal>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
